import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, CheckCircle, MapPin, Calendar, Clock, User, Phone, Mail, CreditCard, ShieldCheck, ArrowLeft } from 'lucide-react';
import logo from '../assets/logo.png';
import { API_URL as API } from '../config/api';

const GREEN = '#084734';
const LIME = '#CEF17B';

export default function VerifyBooking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) {
      setError('Invalid Booking ID');
      setLoading(false);
      return;
    }

    const fetchBooking = async () => {
      try {
        setLoading(true);
        // Fetch from public verify endpoint
        const res = await axios.get(`${API}/bookings/public-verify/${id}`);
        setBooking(res.data);
      } catch (err) {
        console.error('Failed to verify booking:', err);
        setError(err.response?.data?.msg || err.response?.data?.detail || 'Booking not found or invalid.');
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [id]);

  const shortId = booking?.booking_id || (id ? `#${id.slice(-8).toUpperCase()}` : '');

  return (
    <div style={{ minHeight: '100vh', background: '#F4F6F3', fontFamily: "'Inter', sans-serif", paddingBottom: '4rem' }}>
      {/* Header Banner */}
      <div style={{ background: GREEN, padding: '2.5rem 1.5rem', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: 800, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
              <img src={logo} alt="TurfX" style={{ height: 32, filter: 'brightness(0) invert(1)' }} />
            </Link>
            <span style={{
              background: 'rgba(206,241,123,0.15)', border: '1px solid rgba(206,241,123,0.3)',
              color: LIME, padding: '4px 12px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 800,
              display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '1px'
            }}>
              <ShieldCheck size={14} /> Official Verification Pass
            </span>
          </div>

          <h1 style={{ fontSize: '2.2rem', fontWeight: 900, marginBottom: '0.4rem', color: 'white' }}>
            Booking Ticket Verification
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', fontWeight: 500 }}>
            Present this verified ticket at the venue for entrance validation
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: 800, margin: '-2rem auto 0', padding: '0 1.5rem', position: 'relative', zIndex: 2 }}>
        {loading && (
          <div style={{ background: 'white', borderRadius: 24, padding: '4rem 2rem', textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⏳</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#161616' }}>Verifying Booking Ticket...</div>
          </div>
        )}

        {error && !loading && (
          <div style={{ background: 'white', borderRadius: 24, padding: '3rem 2rem', textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
            <h2 style={{ fontSize: '1.6rem', fontWeight: 900, color: '#DC2626', marginBottom: '0.5rem' }}>Verification Failed</h2>
            <p style={{ color: '#4B5563', fontSize: '1rem', marginBottom: '2rem' }}>{error}</p>
            <Link to="/my-bookings" style={{ background: GREEN, color: LIME, padding: '12px 28px', borderRadius: 12, textDecoration: 'none', fontWeight: 800 }}>
              Go to My Bookings
            </Link>
          </div>
        )}

        {booking && !loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Status Card */}
            <div style={{
              background: 'white', borderRadius: 24, padding: '1.5rem 2rem',
              border: '2px solid #BBF7D0', boxShadow: '0 10px 30px rgba(8,71,52,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircle size={28} color="#15803D" />
                </div>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#15803D', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Status: Verified Pass
                  </div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#161616' }}>
                    {booking.status === 'completed' || booking.status === 'checked-in' ? 'Checked-In ✅' : 'Confirmed Booking ✅'}
                  </div>
                </div>
              </div>

              <div style={{ background: '#F8FAF7', border: '1.5px solid #EEF2E6', padding: '10px 18px', borderRadius: 16, textAlign: 'right' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>Booking Unique No.</div>
                <div style={{ fontFamily: 'monospace', fontSize: '1.2rem', fontWeight: 900, color: GREEN }}>
                  {shortId}
                </div>
              </div>
            </div>

            {/* Combined Ticket Details Card */}
            <div style={{ background: 'white', borderRadius: 24, padding: '2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', border: '1.5px solid #EEF2E6' }}>
              
              {/* Turf Section */}
              <div style={{ borderBottom: '1px dashed #E5E7EB', paddingBottom: '1.5rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', fontWeight: 800, color: GREEN, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>
                  <Trophy size={16} /> Turf & Venue Details
                </div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: 900, color: '#161616', marginBottom: '0.5rem' }}>
                  {booking.turf_id?.name || booking.turf_name || 'Sports Venue'}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#4B5563', fontSize: '0.95rem', fontWeight: 600, marginBottom: '1rem' }}>
                  <MapPin size={16} color={GREEN} />
                  <span>{booking.turf_id?.location || booking.turf_location || 'Venue Location'}, {booking.turf_id?.city || booking.turf_city || ''}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ background: '#F3F4F6', color: '#1F2937', padding: '6px 14px', borderRadius: 8, fontSize: '0.85rem', fontWeight: 700 }}>
                    ⚽ Sport: {booking.turf_id?.sport || booking.turf_sport || booking.sport || 'Sports'}
                  </span>
                </div>
              </div>

              {/* Customer Info & Customer ID Section */}
              <div style={{ borderBottom: '1px dashed #E5E7EB', paddingBottom: '1.5rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', fontWeight: 800, color: GREEN, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>
                  <User size={16} /> Customer Identification
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.2rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>Customer Name</div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#111827' }}>
                      {booking.user_name || booking.user_id?.name || 'Customer'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>Customer ID</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 800, color: GREEN, background: '#F8FAF7', padding: '4px 10px', borderRadius: 6, display: 'inline-block' }}>
                      {booking.user_id?._id || booking.user_id?.id || booking.user_id || 'CUST-ID'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>Phone Number</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Phone size={14} color="#6B7280" /> {booking.user_phone || booking.user_id?.phone || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>Email Address</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Mail size={14} color="#6B7280" /> {booking.user_email || booking.user_id?.email || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Slot & Payment Summary */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', fontWeight: 800, color: GREEN, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>
                  <Calendar size={16} /> Booking Schedule & Payment
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.2rem', background: '#F9FAFB', padding: '1.25rem', borderRadius: 16, border: '1px solid #E5E7EB' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>Date of Booking</div>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Calendar size={14} color={GREEN} /> {booking.date}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>Time Slot(s)</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Clock size={14} color={GREEN} />
                      {Array.isArray(booking.time_slots) ? booking.time_slots.join(', ') : booking.time_slot || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>Total Amount Paid</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 900, color: GREEN, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CreditCard size={16} color={GREEN} /> ₹{Number(booking.total_amount ?? booking.total_price ?? booking.totalPaid ?? booking.amount ?? booking.amount_paid ?? booking.court_amount ?? booking.base_amount ?? 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', marginBottom: 4 }}>Payment ID</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 700, color: '#4B5563' }}>
                      {booking.payment_id || booking.razorpay_payment_id || 'DIRECT_BOOKING'}
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Navigation options */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button onClick={() => window.print()} style={{ background: 'white', color: '#111827', border: '1.5px solid #D1D5DB', padding: '14px 24px', borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' }}>
                🖨️ Print Ticket
              </button>
              <Link to="/my-bookings" style={{ background: GREEN, color: LIME, padding: '14px 28px', borderRadius: 12, textDecoration: 'none', fontWeight: 800, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <ArrowLeft size={18} /> My Bookings
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
